import { ButtonType, Dropdown } from "@constellation-academy/epic-ui";
import { useFormik } from "formik";
import { useFragment, useMutation } from "react-relay";
import { useTranslation } from "@i18n/hooks";
import { type videoElementForm_EditS3VideoElementMutation } from "@relay/videoElementForm_EditS3VideoElementMutation.graphql";
import { type videoElementForm_EditVideoElementMutation } from "@relay/videoElementForm_EditVideoElementMutation.graphql";
import { type videoElementForm_VideoElementFragment$key } from "@relay/videoElementForm_VideoElementFragment.graphql";
import {
	createSourceTypeOptions,
	createVideoElementFormSchema,
	getInitialSourceType,
	S3_SOURCE_TYPE,
} from "./video-element-form.consts";
import {
	EDIT_S3_VIDEO_ELEMENT_MUTATION,
	EDIT_VIDEO_ELEMENT_MUTATION,
	VIDEO_ELEMENT_FRAGMENT,
} from "./video-element-form.graphql";
import { buttonWrapperClass, videoInputContainerClass } from "./video-element-form.styles";
import { VideoElementFormProps, VideoElementFormState } from "./video-element-form.types";
import { Button, ButtonVariant } from "../../../../components/button";
import { DefaultTextFieldComponent } from "../../../../components/DefaultTextInput";
import { ValidatedField } from "../../../../components/ValidatedField";
import { VideoSourceDropdown } from "../../../../components/video-source-dropdown";
import {
	addEditedFormToEditedFormsArray,
	resetArrayOfEditedForms,
} from "../../../../store/slices/CoreSlice";
import { useTypedDispatch } from "../../../../store/store.redux";
import { stripHtml } from "../../../../util/html.utils";
import { S3FolderHelp, S3VideoSourceField } from "../s3-video-source-field";
import { VideoSourceField } from "../video-source-field";
import { useDraftStatusUpdater } from "../use-draft-status-updater";

export const VideoElementForm = ({ videoElementFragmentRef, onBack }: VideoElementFormProps) => {
	const { t } = useTranslation(["common", "validation"]);
	const setDraft = useDraftStatusUpdater();
	const element = useFragment<videoElementForm_VideoElementFragment$key>(
		VIDEO_ELEMENT_FRAGMENT,
		videoElementFragmentRef,
	);
	const [editVideoElement, isEditingVideoElement] =
		useMutation<videoElementForm_EditVideoElementMutation>(EDIT_VIDEO_ELEMENT_MUTATION);
	const [editS3VideoElement, isEditingS3VideoElement] =
		useMutation<videoElementForm_EditS3VideoElementMutation>(EDIT_S3_VIDEO_ELEMENT_MUTATION);

	const formId = "VideoElementForm";
	const dispatch = useTypedDispatch();
	const isSaving = isEditingVideoElement || isEditingS3VideoElement;

	const initialSourceType = getInitialSourceType(
		element.videoSource,
		element.availableSourceTypes,
	);
	const sourceTypeOptions = createSourceTypeOptions(
		t,
		element.availableSourceTypes,
		initialSourceType,
	);

	const formik = useFormik<VideoElementFormState>({
		initialValues: {
			title: stripHtml(element.title),
			sourceType: initialSourceType,
			vimeoId: element.videoSource.vimeoId ?? "",
			videoSourceId: element.videoSource.vimeoVideoSource?.id ?? "",
			folder: element.videoSource.folder ?? "",
		},
		validationSchema: createVideoElementFormSchema(t),
		onSubmit: (values) => {
			const onCompleted = () => {
				dispatch(resetArrayOfEditedForms());
			};

			if (values.sourceType === S3_SOURCE_TYPE) {
				editS3VideoElement({
					variables: {
						input: {
							videoElementId: element.id,
							title: values.title,
							folder: values.folder?.trim() ?? "",
						},
					},
					onCompleted,
					updater: setDraft,
				});
				return;
			}

			editVideoElement({
				variables: {
					input: {
						videoElementId: element.id,
						title: values.title,
						vimeoId: values.vimeoId ?? "",
						vimeoVideoSourceId: values.videoSourceId ?? "",
					},
				},
				onCompleted,
				updater: setDraft,
			});
		},
	});

	const isS3Source = formik.values.sourceType === S3_SOURCE_TYPE;

	return (
		<form onSubmit={formik.handleSubmit}>
			<ValidatedField<VideoElementFormState, string>
				name={"title"}
				label={t("general.title")}
				component={DefaultTextFieldComponent}
				onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
				formikConfig={formik}
			/>
			<div className={videoInputContainerClass}>
				<ValidatedField<VideoElementFormState, string>
					name={"sourceType"}
					label={t("forms.videoSourceType")}
					onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
					component={({ fieldValue, updateField, onChange }) => {
						return (
							<Dropdown
								options={sourceTypeOptions}
								value={fieldValue}
								onChange={(value: string) => {
									updateField(value);
									onChange?.();
								}}
							/>
						);
					}}
					formikConfig={formik}
				/>
				{isS3Source ? (
					<ValidatedField<VideoElementFormState, string>
						name={"folder"}
						label={t("forms.videoFolder")}
						labelSuffix={<S3FolderHelp />}
						onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
						component={({ fieldValue, updateField, onChange }) => {
							return (
								<S3VideoSourceField
									videoElementId={element.id}
									onChange={onChange}
									onUpdate={updateField}
									folder={fieldValue}
								/>
							);
						}}
						formikConfig={formik}
					/>
				) : (
					<>
						<ValidatedField<VideoElementFormState, string>
							name={"videoSourceId"}
							label={t("forms.videoSource")}
							onChange={() =>
								dispatch(addEditedFormToEditedFormsArray({ form: formId }))
							}
							component={({ fieldValue, updateField, onChange }) => {
								return (
									<VideoSourceDropdown
										fieldValue={fieldValue}
										updateField={updateField}
										onChange={onChange}
									/>
								);
							}}
							formikConfig={formik}
						/>
						<ValidatedField<VideoElementFormState, string>
							name={"vimeoId"}
							label={t("forms.video")}
							onChange={() =>
								dispatch(addEditedFormToEditedFormsArray({ form: formId }))
							}
							component={({ fieldValue, updateField, onChange, ...props }) => {
								return (
									<VideoSourceField
										onChange={onChange}
										onUpdate={updateField}
										vimeoId={fieldValue}
										videoSourceId={formik.values.videoSourceId}
										{...props}
									/>
								);
							}}
							formikConfig={formik}
						/>
					</>
				)}
			</div>
			<div className={buttonWrapperClass}>
				<Button
					disabled={isSaving}
					type={ButtonType.Submit}
					variant={ButtonVariant.Strong}
					label={t("actions.save")}
					stretch
				/>
				<Button
					type={ButtonType.Submit}
					onClick={onBack}
					label={t("actions.back")}
					variant={ButtonVariant.Tertiary}
					stretch
				/>
			</div>
		</form>
	);
};
